// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useRef, useEffect, useId, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { DayPicker } from 'react-day-picker'
import { format, isValid, getYear, setYear, setMonth, addMonths, subMonths } from 'date-fns'
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import 'react-day-picker/style.css'

interface DateInputProps {
  value: string
  onChange: (iso: string) => void
  label?: React.ReactNode
  disabled?: boolean
  error?: boolean
  className?: string
}

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

const THIS_YEAR = new Date().getFullYear()
const YEARS = Array.from({ length: THIS_YEAR - 1899 }, (_, i) => THIS_YEAR - i)

function parseIso(iso: string): Date | undefined {
  if (!iso) return undefined
  const d = new Date(iso + 'T00:00:00')
  return isValid(d) ? d : undefined
}

interface PopoverPos { top: number; left: number; width: number; maxHeight: number }

export function DateInput({ value, onChange, label, disabled, error, className }: DateInputProps) {
  const id = useId()
  const selected = parseIso(value)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<PopoverPos>({ top: 0, left: 0, width: 300, maxHeight: 380 })
  const [calMonth, setCalMonth] = useState<Date>(selected ?? new Date())
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const updatePos = useCallback(() => {
    if (!triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    const MARGIN = 8
    const DESIRED_H = 380
    const width = Math.max(r.width, 300)
    // Place on whichever side has more room; cap height to that space (calendar scrolls
    // if constrained) and clamp into the viewport so a short window never bleeds off-screen.
    const spaceBelow = window.innerHeight - r.bottom - MARGIN
    const spaceAbove = r.top - MARGIN
    const below = spaceBelow >= spaceAbove
    const maxHeight = Math.min(DESIRED_H, Math.max(below ? spaceBelow : spaceAbove, 200))
    let top = below ? r.bottom + 6 : r.top - maxHeight - 6
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - maxHeight - MARGIN))
    const left = Math.max(MARGIN, Math.min(r.left, window.innerWidth - width - MARGIN))
    setPos({ top, left, width, maxHeight })
  }, [])

  function openCalendar() {
    updatePos()
    setOpen(true)
  }

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, updatePos])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      const target = e.target as Node
      if (
        popoverRef.current && !popoverRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function handler(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  function handleSelect(day: Date | undefined) {
    if (day) onChange(format(day, 'yyyy-MM-dd'))
    setOpen(false)
  }

  const displayValue = selected ? format(selected, 'MMMM d, yyyy') : ''

  const popover = open ? (
    <div
      ref={popoverRef}
      role="dialog"
      aria-modal="true"
      aria-label="Date picker"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight, overflowY: 'auto', zIndex: 9999 }}
      className="rounded-2xl border border-border bg-card shadow-2xl shadow-black/20 animate-in fade-in slide-in-from-top-2 duration-150"
    >
      {/* Month / Year nav */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-secondary/40">
        <button
          type="button"
          onClick={() => setCalMonth(prev => subMonths(prev, 1))}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-1.5">
          <select
            value={calMonth.getMonth()}
            onChange={e => setCalMonth(prev => setMonth(prev, parseInt(e.target.value, 10)))}
            className="bg-transparent text-sm font-semibold text-foreground outline-none cursor-pointer hover:text-primary transition-colors"
            aria-label="Month"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i} className="bg-card text-foreground">{m}</option>
            ))}
          </select>

          <select
            value={getYear(calMonth)}
            onChange={e => setCalMonth(prev => setYear(prev, parseInt(e.target.value, 10)))}
            className="bg-transparent text-sm font-semibold text-foreground outline-none cursor-pointer hover:text-primary transition-colors"
            aria-label="Year"
          >
            {YEARS.map(y => (
              <option key={y} value={y} className="bg-card text-foreground">{y}</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => setCalMonth(prev => addMonths(prev, 1))}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Calendar grid */}
      <div className="px-3 py-3">
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          month={calMonth}
          onMonthChange={setCalMonth}
          disabled={{ after: new Date() }}
          showOutsideDays
          classNames={{
            months: 'flex flex-col',
            month: 'w-full',
            month_caption: 'hidden',
            nav: 'hidden',
            month_grid: 'w-full border-collapse',
            weekdays: 'flex',
            weekday: 'flex-1 text-center text-[11px] font-semibold text-muted-foreground pb-2',
            week: 'flex mt-1',
            day: 'flex-1 flex items-center justify-center',
            day_button: cn(
              'w-8 h-8 rounded-lg text-[13px] font-medium transition-all duration-150',
              'text-foreground hover:bg-primary/10 hover:text-primary',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground',
            ),
            selected: 'rdp-sel',
            today: 'rdp-today',
            outside: 'opacity-30',
          }}
        />
      </div>

      <style>{`
        .rdp-sel .rdp-day_button {
          background: linear-gradient(135deg, #006EF9, #02C7C3) !important;
          color: white !important;
          font-weight: 700;
          border-radius: 0.5rem;
        }
        .rdp-today:not(.rdp-sel) .rdp-day_button {
          color: #006EF9 !important;
          font-weight: 700;
        }
      `}</style>
    </div>
  ) : null

  return (
    <div className={cn('relative flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
        </label>
      )}

      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={openCalendar}
        className={cn(
          'w-full h-11 px-3 rounded-xl border bg-secondary dark:bg-input-bg flex items-center gap-2.5 text-sm transition-all duration-200 text-left',
          open    ? 'border-primary ring-1 ring-primary/30'
          : error ? 'border-destructive ring-1 ring-destructive/20 hover:border-destructive'
          :         'border-border hover:border-primary/50',
          displayValue ? 'text-foreground' : 'text-muted-foreground',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="flex-1">{displayValue || 'Pick a date'}</span>
        <ChevronDown
          className={cn('h-4 w-4 text-muted-foreground transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {createPortal(popover, document.body)}
    </div>
  )
}
